<script setup>
import { useStepper } from '@/composables/useStepper';
import { onMounted, provide, reactive, ref, watch } from 'vue';
import DetailForm from '@/modules/Signup/views/sign-up-form/DetailForm.vue';
import PaymentMethodForm from '@/modules/Signup/views/sign-up-form/PaymentMethodForm.vue';
import ConfirmationForm from '@/modules/Signup/views/sign-up-form/ConfirmationForm.vue';
import ActivationProcessForm from '@/modules/Signup/views/sign-up-form/ActivationProcessForm.vue';
import VButton from '@/modules/Signup/components/VButton.vue';
import { useSignUpModuleStore } from '@/stores/modules/signup/signUpStore';
import { safeTrim } from '@/utilities/stringUtils';
import { useRoute, useRouter } from 'vue-router';
import ThankYouView from '@/modules/Signup/views/sign-up-form/ThankYouView.vue';
import { retrieveAndDecryptAccountData } from '@/modules/Signup/services/signupService';
import { storeToRefs } from 'pinia/dist/pinia';
import VLoader from '@/modules/Signup/components/VLoader.vue';
import { useVLoader } from '@/modules/Signup/composables/useVLoader';

const
    router = useRouter(),
    route = useRoute();

const
    showOrderingOptionsModal = ref(false),
    orderingOptionsSelected = ref(false);

const
    signUpStore = useSignUpModuleStore(),
    {
        selectedPlanId,
        selectedModemId,
        selectedNbnLocation,
    } = storeToRefs(signUpStore);

const { isLoading, loadingText, withLoading } = useVLoader();

// Initialize the form fields which will be used by the child components. This is just the raw form for validation,
// etc. The final payload will be updated upon submission.

// TODO: refactor, move this to pinia for single source of truth, provide/inject overhead
const form = reactive({
    // Details
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    dob: '',
    billing_address: '',
    custom_billing_address: '', // holds the address that the user manually added.
    shipping_method: '',
    shipping_address: '',
    custom_shipping_address: '',
    has_existing_connection: false,

    /** Payment Methods - Same info for upfront and recurring */
    selectedPaymentMethod: '',
    // Bank Account
    bankId: '',
    bankAccountName: '',
    bankAccountNumber: '',
    bankBsb: '',
    // Credit Card
    ccCardType: '',
    ccCardNumber: '',
    ccCardName: '',
    ccCvv: '',
    ccExpiryDate: '',

    /** Payment Methods - Recurring is different from the upfront */
    recurring: {
        selectedPaymentMethod: null,
        // Bank Account
        bankId: '',
        bankAccountName: '',
        bankAccountNumber: '',
        bankBsb: '',
        // Credit Card
        ccCardType: '',
        ccCardNumber: '',
        ccCardName: '',
        ccCvv: '',
        ccExpiryDate: '',
    },

    // Submit
    // TODO: Not sure what are the names and if each of them are required, so I just put it as placeholder.
    agreements: {},
});
provide('form', form);

const steps = [
    {
        key: 'details',
        title: 'Details',
        panelInfo: {
            title: 'Enter your details',
            subtitle: 'You\'re almost there',
        },
        component: DetailForm,
        isValid: () => safeTrim(form.firstname) && safeTrim(form.lastname) && safeTrim(form.email) && safeTrim(form.phone),
        process: async () => {
            // Trigger account/store and simple-nbn-sq APIs
            const detailFields = [
                'firstname', 'lastname', 'email', 'phone', 'dob',
                'billing_address', 'custom_billing_address', 'shipping_method',
                'shipping_address', 'custom_shipping_address', 'has_existing_connection',
            ];

            const payload = Object.fromEntries(
                Object.entries(form).filter(([key]) => detailFields.includes(key)),
            );

            try {
                // TODO: Persist data as its being called everytime the "next" button is triggered.
                const { operation, loadingText } = await signUpStore.processAccountCreation(payload);
                const isAccountCreated = await withLoading(operation, loadingText);

                if (!isAccountCreated) {
                    console.warn('Account creation failed');
                    return false;
                }

                // Process ordering options and show the modal
                await withLoading(async () => {
                    await signUpStore.processOrderingOptions();
                    showOrderingOptionsModal.value = true;
                }, 'Processing ordering options. Please wait ...');
                return true;

            } catch (error) {
                console.error('Error in process function:', error);
                return false;
            }
        },
    },
    {
        key: 'activation-process',
        title: 'Details',
        panelInfo: {
            title: 'Enter your details',
            subtitle: 'You\'re almost there',
        },
        component: ActivationProcessForm,
        hideFromStepper: true,
        isValid: () => true, // TODO: should require avcId and ntdid
        process: async () => {
            try {
                // Trigger initiate order
                const { operation, loadingText } = await signUpStore.processInitiateOrder();
                const isOrderInitiated = await withLoading(operation, loadingText);

                if (!isOrderInitiated) {
                    // Redirect to ErrorView
                    await router.push({ name: 'error' });
                    // TODO: should probably clear data if an error occurred.
                    return false;
                }

                return true;
            } catch (error) {
                console.error('Error in process function:', error);
                return false;
            }
        },
    },
    {
        key: 'payment-method',
        title: 'Payment Method',
        panelInfo: {
            title: 'Enter your payment details',
        },
        component: PaymentMethodForm,
        isValid: () => {
            // TODO: Exclude the payment_method, just the fields under it
            return true;
        },
        process: async () => {
            try {
                const { operation, loadingText } = await signUpStore.processSelectedPaymentMethod();
                await withLoading(operation, loadingText);
                return true;
            } catch (error) {
                console.error(error);
                return false;
            }
        },
    },
    {
        key: 'submit',
        title: 'Submit',
        panelInfo: {
            title: 'Confirm and Submit',
        },
        component: ConfirmationForm,
        // isValid: () => form.agreement_1 && form.agreement_2,
        isValid: () => true,
        process: async () => {
            try {
                await signUpStore.processPerformPayment();
                return true;
            } catch (error) {
                console.error(error);
                return false;
            }
        },
    },
    {
        key: 'thank-you',
        component: ThankYouView,
        // This step will still be included from the flow but will be hidden from the stepper (header part)
        hideFromStepper: true,
    },
];
const stepper = useStepper(steps);

async function submit() {
    const step = stepper.currentStep.value;

    if (!step.isValid?.()) return;

    /** If a process exists, run before proceeding. */
    if (step.process && !(step.key === 'details' && orderingOptionsSelected.value)) {
        try {
            const processResult = await step.process();
            if (processResult === false) {
                alert('There was an error processing your request. Please try again.');
                return;
            }

            // If we're on the details step and the ordering options modal is open,
            // and we're not coming from a confirmed selection,
            // don't proceed to the next step yet - wait for user interaction with the modal
            if (step.key === 'details' && showOrderingOptionsModal.value) {
                console.log('Ordering options modal is open, waiting for user selection');
                return;
            }
        } catch (error) {
            console.error('Step process failed:', error);
            alert('There was an error processing your request. Please try again.');
            return;
        }
    }

    if (!stepper.isLastActionableStep.value) {
        stepper.goToNext();
    } else {
        await signUpStore.register(form);
        stepper.goToStep('thank-you');
    }
}

/**
 * Redirect appropriately based on the query params.
 *
 * TODO: refactor this to a more generic solution. Probably include this to the stepper composable
 */
function back() {
    if (
        route.query.from === 'details'
        && ['activation-process', 'payment-method'].includes(route.query.next)
    ) {
        stepper.goToStep('details');
    } else {
        stepper.goToPrevious();
    }
}

onMounted(async () => {
    const retrievedAccountData = await retrieveAndDecryptAccountData();
    if (retrievedAccountData) {
        // Map retrieved data to existing state management
        selectedPlanId.value = retrievedAccountData.planId;
        selectedModemId.value = retrievedAccountData.modemId;
        selectedNbnLocation.value = retrievedAccountData.directoryID;
    } else {
        console.warn('No account data retrieved.');
        // The user will need to go through the account creation process again
    }

    if (route.query.from === 'details' && route.query.next === 'activation-process') {
        stepper.goToStep('activation-process');
    }

    if (route.query.from === 'details' && route.query.next === 'payment-method') {
        stepper.goToStep('payment-method');
    }

    if (route.query.from === 'payment-method' && route.query.next === 'submit') {
        stepper.goToStep('submit');
    }

    signUpStore.getPaymentMethods();

    await withLoading(async () => {
        await signUpStore.getTermsAndConditions();
    }, 'Preparing, please wait ...');
});

watch(
    () => route.query,
    (query) => {
        if (query.from === 'details' && query.next === 'activation-process') {
            stepper.goToStep('activation-process');
        }

        if (query.from === 'details' && query.next === 'payment-method') {
            stepper.goToStep('payment-method');
        }

        if (query.from === 'payment-method' && query.next === 'submit') {
            stepper.goToStep('submit');
        }
    },
);
</script>

<template>
    <div class="tw-w-2/3 tw-mx-auto">
        <div class="tw-flex tw-py-16">
            <div v-for="step in stepper.steps" :key="step.key" class="tw-flex-1">
                <span>{{ step.title }}</span>
            </div>
        </div>

        <template v-if="!stepper.isThankYouPage.value">
            <form class="mt-10" @submit.prevent="submit" novalidate>
                <div class="tw-mb-16">
                    <span class="tw-text-[1.2em] tw-font-bold tw-text-primary tw-mb-1 tw-inline-block">
                        {{ stepper.currentStep.value.panelInfo?.subtitle || '&nbsp;' }}
                    </span>
                    <h4 class="!tw-text-[2em] !tw-font-bold tw-font-neue-kabel-bold">
                        {{ stepper.currentStep.value.panelInfo?.title }}
                    </h4>
                </div>

                <component
                    :is="stepper.currentStep.value.component"
                    v-model:showOrderingOptionsModal="showOrderingOptionsModal"
                    v-model:orderingOptionsSelected="orderingOptionsSelected"
                    @selectionConfirmed="submit"
                />

                <div class="tw-mt-12 tw-flex tw-justify-between">
                    <VButton
                        v-if="stepper.isFirst.value"
                        label="Continue Shopping"
                        @click="() => router.push({ name: 'plans-and-pricing' })"
                        bg-class="!tw-bg-[#ADABC6]"
                    />
                    <VButton
                        v-if="!stepper.isFirst.value"
                        label="Back"
                        @click="back"
                        bg-class="!tw-bg-[#ADABC6]"
                    />

                    <VButton v-if="!stepper.isLastActionableStep.value" label="Next" type="submit" />
                    <VButton v-else label="Submit" type="submit" />
                </div>
            </form>
        </template>

        <template v-else>
            <component :is="stepper.currentStep.value.component" />
        </template>
    </div>

    <!-- Show loader during route transitions -->
    <VLoader
        v-if="isLoading"
        :text="loadingText"
    />
</template>
